import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Snowflake, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(next);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-sky-500 border-2 border-slate-900 shadow-[4px_4px_0px_0px_#0f172a] flex items-center justify-center">
            <Snowflake className="w-7 h-7 text-white" />
          </div>
          <div>
            <div className="font-display text-2xl uppercase leading-none text-slate-900">
              On <span className="bg-sky-500 text-white px-1.5 -skew-x-3 inline-block">Ice</span>
            </div>
            <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] text-slate-500 mt-1">
              Sligo & Clonmel · Booking Office
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="card-brutal shadow-[8px_8px_0px_0px_#0f172a] p-6 md:p-8" data-testid="login-form">
          <h1 className="font-display text-2xl uppercase text-slate-900 mb-1">Staff sign in</h1>
          <p className="text-sm text-slate-600 mb-6">Admin & rink staff access only.</p>

          {error && (
            <div className="border-2 border-rose-500 bg-rose-50 text-rose-700 p-3 font-mono-tight text-xs font-bold mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          <label className="label-brutal" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            required
            autoComplete="email"
            className="input-brutal mb-4"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label className="label-brutal" htmlFor="login-password">Password</label>
          <input
            id="login-password"
            data-testid="login-password-input"
            type="password"
            required
            autoComplete="current-password"
            className="input-brutal mb-6"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="login-submit-button">
            <LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="font-mono-tight text-xs uppercase tracking-widest font-bold text-sky-600 hover:text-sky-800 underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
